import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usersApi, tenantsApi } from '@/lib/api'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import {
  Plus, Edit, Trash2, Search, RefreshCw,
  Smartphone, CheckCircle, XCircle,
  KeyRound, ToggleLeft, ToggleRight, Filter, Building2,
} from 'lucide-react'
import { formatDateTime } from '@/lib/utils'
import type { User, TenantAccess } from '@/types'

const ROLE_COLORS: Record<string, string> = {
  SUPER_ADMIN: 'bg-red-100 text-red-800',
  TENANT_ADMIN: 'bg-purple-100 text-purple-800',
  QUALITY_MANAGER: 'bg-blue-100 text-blue-800',
  PRODUCTION_MANAGER: 'bg-indigo-100 text-indigo-800',
  SUPERVISOR: 'bg-cyan-100 text-cyan-800',
  OPERATOR: 'bg-green-100 text-green-800',
  AUDITOR: 'bg-yellow-100 text-yellow-800',
  VIEWER: 'bg-gray-100 text-gray-700',
}

function roleBadge(name: string) {
  const cls = ROLE_COLORS[name] ?? 'bg-gray-100 text-gray-700'
  return (
    <span key={name} className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {name}
    </span>
  )
}

// ─── Reset Password Modal ──────────────────────────────────────────────────
function ResetPasswordModal({
  user,
  onClose,
}: {
  user: User
  onClose: () => void
}) {
  const [pwd, setPwd] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const qc = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => usersApi.resetPassword(user.id, pwd),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); onClose() },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Erro ao redefinir'),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (pwd !== confirm) { setError('As passwords não coincidem'); return }
    if (pwd.length < 8) { setError('Mínimo 8 caracteres'); return }
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
        <h2 className="mb-1 text-lg font-semibold text-gray-900">Redefinir Password</h2>
        <p className="mb-4 text-sm text-gray-500">{user.first_name} {user.last_name}</p>
        {error && <p className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        <form onSubmit={submit} className="space-y-3">
          <input
            type="password" placeholder="Nova password" value={pwd}
            onChange={e => setPwd(e.target.value)}
            className="input w-full" required
          />
          <input
            type="password" placeholder="Confirmar password" value={confirm}
            onChange={e => setConfirm(e.target.value)}
            className="input w-full" required
          />
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">
              {mutation.isPending ? 'A guardar...' : 'Confirmar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Delete Confirm Modal ──────────────────────────────────────────────────
function DeleteModal({ user, onClose }: { user: User; onClose: () => void }) {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => usersApi.delete(user.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); onClose() },
  })
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
        <h2 className="mb-2 text-lg font-semibold text-gray-900">Desativar utilizador?</h2>
        <p className="mb-4 text-sm text-gray-600">
          <strong>{user.first_name} {user.last_name}</strong> será desativado e não poderá iniciar sessão.
        </p>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn btn-secondary flex-1">Cancelar</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="btn btn-danger flex-1"
          >
            {mutation.isPending ? 'A desativar...' : 'Desativar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Tenant Access Modal ───────────────────────────────────────────────────
function TenantAccessModal({ user, onClose }: { user: User; onClose: () => void }) {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [selectedTenantId, setSelectedTenantId] = useState<string>('')

  const { data, isLoading } = useQuery({
    queryKey: ['user-tenant-access', user.id],
    queryFn: () => usersApi.getTenantAccess(user.id),
  })
  const entries: TenantAccess[] = data?.data ?? []

  const { data: tenantsData } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenantsApi.list({ page: 1, page_size: 100 }),
  })
  const allTenants = tenantsData?.data?.items ?? []
  const existingTenantIds = new Set(entries.map(e => e.tenant_id))
  const availableTenants = allTenants.filter(t => !existingTenantIds.has(t.id))

  const toggleMutation = useMutation({
    mutationFn: ({ userId, tenantId }: { userId: number; tenantId: number }) =>
      usersApi.toggleTenantAccess(userId, tenantId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-tenant-access', user.id] }),
  })

  const addMutation = useMutation({
    mutationFn: (tenantId: number) => usersApi.addToTenant(user.id, tenantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-tenant-access', user.id] })
      setAdding(false)
      setSelectedTenantId('')
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Acesso a Empresas</h2>
            <p className="text-sm text-gray-500">{user.first_name} {user.last_name} · {user.email}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">✕</button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : entries.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">Sem acessos registados</p>
        ) : (
          <div className="space-y-2">
            {entries.map((e) => (
              <div
                key={e.tenant_id}
                className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">{e.tenant_name}</p>
                    <p className="text-xs text-gray-400">
                      {e.roles.join(', ') || 'Sem role'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => toggleMutation.mutate({ userId: user.id, tenantId: e.tenant_id })}
                  disabled={toggleMutation.isPending}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                    e.is_active ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                  title={e.is_active ? 'Revogar acesso' : 'Conceder acesso'}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      e.is_active ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add to new tenant */}
        {availableTenants.length > 0 && (
          <div className="mt-4 border-t pt-4">
            {!adding ? (
              <button
                onClick={() => setAdding(true)}
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-800 font-medium"
              >
                <Plus className="h-4 w-4" />
                Adicionar a outra empresa
              </button>
            ) : (
              <div className="flex gap-2">
                <select
                  value={selectedTenantId}
                  onChange={e => setSelectedTenantId(e.target.value)}
                  className="input flex-1 text-sm"
                >
                  <option value="">Selecionar empresa...</option>
                  {availableTenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <button
                  onClick={() => selectedTenantId && addMutation.mutate(Number(selectedTenantId))}
                  disabled={!selectedTenantId || addMutation.isPending}
                  className="btn btn-primary py-1 px-3 text-sm"
                >
                  {addMutation.isPending ? '...' : 'Adicionar'}
                </button>
                <button
                  onClick={() => { setAdding(false); setSelectedTenantId('') }}
                  className="btn btn-secondary py-1 px-3 text-sm"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button onClick={onClose} className="btn btn-secondary">Fechar</button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────
export default function UsersPage() {
  const qc = useQueryClient()
  const { user: authUser } = useAuth()
  const isSuperAdmin = authUser?.roles?.some(r => r.name === 'SUPER_ADMIN') ||
    authUser?.permissions?.includes('*') || false

  const [search, setSearch] = useState('')
  const [filterActive, setFilterActive] = useState<'' | 'true' | 'false'>('')
  const [tenantFilter, setTenantFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [resetUser, setResetUser] = useState<User | null>(null)
  const [deleteUser, setDeleteUser] = useState<User | null>(null)
  const [accessUser, setAccessUser] = useState<User | null>(null)

  const { data: tenantsData } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenantsApi.list({ page: 1, page_size: 100 }),
    enabled: isSuperAdmin,
  })
  const tenants = tenantsData?.data?.items ?? []

  const { data, isLoading, isError } = useQuery({
    queryKey: ['users', page, search, filterActive, tenantFilter],
    queryFn: () =>
      usersApi.list({
        page,
        page_size: 20,
        search: search || undefined,
        is_active: filterActive === '' ? undefined : filterActive === 'true',
        tenant_id: tenantFilter ? Number(tenantFilter) : undefined,
      }),
  })

  const toggleMutation = useMutation({
    mutationFn: (id: number) => usersApi.toggleActive(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })

  const users = data?.data?.items ?? []
  const total = data?.data?.total ?? 0
  const totalPages = data?.data?.total_pages ?? 1

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Utilizadores</h1>
          <p className="mt-1 text-sm text-gray-500">
            {total} utilizador{total !== 1 ? 'es' : ''} no tenant
          </p>
        </div>
        <Link to="/users/new" className="btn btn-primary">
          <Plus className="mr-2 h-4 w-4" />
          Novo Utilizador
        </Link>
      </div>

      {/* Filters bar */}
      <div className="mt-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Pesquisar por nome ou email..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            className="input pl-9 w-full"
          />
        </div>
        <div className="flex items-center gap-1">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={filterActive}
            onChange={e => { setFilterActive(e.target.value as any); setPage(1) }}
            className="input"
          >
            <option value="">Todos</option>
            <option value="true">Ativos</option>
            <option value="false">Inativos</option>
          </select>
        </div>
        {isSuperAdmin && tenants.length > 0 && (
          <select
            value={tenantFilter}
            onChange={e => { setTenantFilter(e.target.value); setPage(1) }}
            className="input"
          >
            <option value="">Todos os tenants</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        )}
        <button
          onClick={() => qc.invalidateQueries({ queryKey: ['users'] })}
          className="btn btn-secondary"
          title="Atualizar"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Table */}
      <div className="mt-4 card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : isError ? (
          <p className="py-12 text-center text-sm text-red-500">Erro ao carregar utilizadores</p>
        ) : users.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-500">Nenhum utilizador encontrado</p>
        ) : (
          <table className="table w-full">
            <thead>
              <tr>
                <th>Utilizador</th>
                <th>Roles</th>
                <th className="text-center">Mobile</th>
                <th className="text-center">Estado</th>
                <th>Último Login</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className={!user.is_active ? 'opacity-50' : ''}>
                  {/* Name + email */}
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
                        {user.first_name[0]}{user.last_name[0]}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </td>

                  {/* Roles */}
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {user.roles.length === 0 ? (
                        <span className="text-xs text-gray-400">Sem role</span>
                      ) : (
                        user.roles.map(r => roleBadge(r.name))
                      )}
                    </div>
                  </td>

                  {/* Mobile access */}
                  <td className="text-center">
                    {user.mobile_access ? (
                      <span title="Acesso mobile ativo"><Smartphone className="inline h-4 w-4 text-green-500" /></span>
                    ) : (
                      <span title="Sem acesso mobile"><Smartphone className="inline h-4 w-4 text-gray-300 opacity-40" /></span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="text-center">
                    {user.is_active ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                        <CheckCircle className="h-3 w-3" /> Ativo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        <XCircle className="h-3 w-3" /> Inativo
                      </span>
                    )}
                  </td>

                  {/* Last login */}
                  <td className="text-sm text-gray-500">
                    {user.last_login_at ? formatDateTime(user.last_login_at) : '—'}
                  </td>

                  {/* Actions */}
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      {/* Edit */}
                      <Link
                        to={`/users/${user.id}/edit`}
                        className="rounded p-1.5 text-blue-600 hover:bg-blue-50"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </Link>

                      {/* Toggle active */}
                      <button
                        onClick={() => toggleMutation.mutate(user.id)}
                        disabled={toggleMutation.isPending}
                        className="rounded p-1.5 text-yellow-600 hover:bg-yellow-50"
                        title={user.is_active ? 'Desativar' : 'Ativar'}
                      >
                        {user.is_active
                          ? <ToggleRight className="h-4 w-4" />
                          : <ToggleLeft className="h-4 w-4" />}
                      </button>

                      {/* Reset password */}
                      <button
                        onClick={() => setResetUser(user)}
                        className="rounded p-1.5 text-gray-600 hover:bg-gray-100"
                        title="Redefinir password"
                      >
                        <KeyRound className="h-4 w-4" />
                      </button>

                      {/* Tenant access */}
                      <button
                        onClick={() => setAccessUser(user)}
                        className="rounded p-1.5 text-blue-500 hover:bg-blue-50"
                        title="Acesso a Empresas"
                      >
                        <Building2 className="h-4 w-4" />
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => setDeleteUser(user)}
                        className="rounded p-1.5 text-red-600 hover:bg-red-50"
                        title="Desativar utilizador"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <span>Página {page} de {totalPages}</span>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className="btn btn-secondary py-1 px-3 disabled:opacity-40"
            >
              Anterior
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
              className="btn btn-secondary py-1 px-3 disabled:opacity-40"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {resetUser && (
        <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />
      )}
      {deleteUser && (
        <DeleteModal user={deleteUser} onClose={() => setDeleteUser(null)} />
      )}
      {accessUser && (
        <TenantAccessModal user={accessUser} onClose={() => setAccessUser(null)} />
      )}
    </div>
  )
}

